import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../services/api';
import { useCity } from '../contexts/CityContext';
import styles from './BusLineDetailPage.module.css';
import LineChip from '../components/common/LineChip';
import { MdTram } from 'react-icons/md';
import { processTimetables } from '../utils/transportUtils';

// TramLine için bir tip tanımı ekliyoruz - BusLine tipine benzer şekilde
interface TramLine {
  tram_line_id: string;
  tram_line_name: string;
  tram_line_path_description: string;
  description: string;
  start_tram_stop: string;
  end_tram_stop: string;
  tram_line_color: string;
  active: boolean;
  operator: {
    operator_id: number;
    operator_name: string;
    operator_logo?: string;
    operator_website?: string;
    operator_phone?: string;
  };
  stops?: {
    stop_id: string;
    stop_name: string;
    order: number;
    latitude?: number;
    longitude?: number;
  }[];
  live_vehicles?: any[];
  tram_line_announcements?: any[];
  timetables?: any[];
  schedule?: {
    weekday: string[];
    saturday: string[];
    sunday: string[];
  };
  fare?: {
    regular: number;
    student: number;
    senior: number;
    handicapped: number;
  };
  features?: {
    accessibility: boolean;
    wifi: boolean;
    usb: boolean;
    airConditioned: boolean;
  };
  path_coordinates?: [number, number][];
  paths?: {
    direction: 'forward' | 'backward' | 'circular';
    encoded_polyline: string;
    decoded_path?: [number, number][];
    color?: string;
    stops?: string[];
  }[];
  is_express?: boolean;
  duration?: number;
  distance?: number;
  frequency?: number;
}

function TramLineDetailPage() {
  const { lineId } = useParams<{ lineId: string }>();
  const [lineDetail, setLineDetail] = useState<TramLine | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const api = useApi();
  const { currentCity, loading: cityLoading } = useCity();
  const navigate = useNavigate();

  const transportMode = 'tram';

  const getModeIcon = () => <MdTram />;
  const getModeTitle = () => 'Tramvay';

  const fetchLineDetail = useCallback(async () => {
    if (!lineId || !currentCity) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.getTransportLine(transportMode, lineId);

      if (response && response.status === 'success' && response.data) {
        const lineData = response.data;

        if (lineData.timetables && lineData.timetables.length > 0) {
          lineData.schedule = processTimetables(lineData.timetables);
        }

        setLineDetail(lineData);
      } else {
        setError('Tramvay hattı bilgisi bulunamadı.');
      }
    } catch (err) {
      setError('Tramvay hattı bilgisi yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [lineId, transportMode, currentCity, api]);

  useEffect(() => {
    if (!cityLoading) {
      fetchLineDetail();
    }
  }, [fetchLineDetail, cityLoading]);

  return (
    <div className={styles.lineDetailPage}>
      <div className={styles.backButtonContainer}>
        <button className={styles.backButton} onClick={() => navigate('/lines')}>
          ← Hatlara Dön
        </button>
      </div>

      {loading && <div className={styles.loadingMessage}>Tramvay hattı detayları yükleniyor...</div>}

      {error && <div className={styles.errorMessage}>{error}</div>}

      {!loading && !error && lineDetail && (
        <>
          <div className={styles.lineHeader}>
            <div className={styles.modeIndicator}>
              {getModeIcon()}
              <span>{getModeTitle()}</span>
            </div>
            <LineChip lineNumber={lineDetail.tram_line_id} size="large" lineColor={lineDetail.tram_line_color || undefined} />
            <h1 className={styles.lineName}>{lineDetail.tram_line_name}</h1>
          </div>

          <div className={styles.lineDetailsCard}>
            <div className={styles.linePathDescription}>
              <h3>Güzergah Açıklaması</h3>
              <p>{lineDetail.tram_line_path_description}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TramLineDetailPage;